import React, { useEffect, useMemo } from 'react'
import { BiTime } from 'react-icons/bi'

interface SessionDurationRangeProps {
  minDuration: number // in minutes
  maxDuration: number // in minutes
  onMinDurationChange: (minutes: number) => void
  onMaxDurationChange: (minutes: number) => void
  isDarkMode?: boolean
}

// Generate options for minimum duration: 30 minutes to 3 hours (180 minutes) in 15-minute intervals
const generateMinOptions = () => {
  const options = []
  for (let minutes = 30; minutes <= 180; minutes += 15) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    let label = ''
    if (hours === 0) {
      label = `${minutes} min`
    } else if (mins === 0) {
      label = `${hours}h`
    } else {
      label = `${hours}h ${mins}min`
    }
    options.push({ value: minutes, label })
  }
  return options
}

// Generate options for maximum duration: 1 hour (60 minutes) to 4 hours (240 minutes) in 15-minute intervals
const generateMaxOptions = () => {
  const options = []
  for (let minutes = 60; minutes <= 240; minutes += 15) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    let label = ''
    if (hours === 0) {
      label = `${minutes} min`
    } else if (mins === 0) {
      label = `${hours}h`
    } else {
      label = `${hours}h ${mins}min`
    }
    options.push({ value: minutes, label })
  }
  return options
}

const SessionDurationRange: React.FC<SessionDurationRangeProps> = ({
  minDuration,
  maxDuration,
  onMinDurationChange,
  onMaxDurationChange,
}) => {
  const minOptions = useMemo(() => generateMinOptions(), [])
  const maxOptions = useMemo(() => generateMaxOptions(), [])

  // Ensure max is always >= max(60 minutes, minDuration)
  // Max should always be at least 1 hour (60 minutes) as per requirements
  useEffect(() => {
    const minAllowedMax = Math.max(60, minDuration)
    if (maxDuration < minAllowedMax) {
      onMaxDurationChange(minAllowedMax)
    }
  }, [minDuration, maxDuration, onMaxDurationChange])

  const handleMinChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMin = parseInt(e.target.value, 10)
    onMinDurationChange(newMin)
    // If new min is greater than max, update max to at least max(60, newMin)
    const minAllowedMax = Math.max(60, newMin)
    if (maxDuration < minAllowedMax) {
      onMaxDurationChange(minAllowedMax)
    }
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMax = parseInt(e.target.value, 10)
    // Ensure max is not less than min, and at least 60 minutes (1 hour)
    const minAllowedMax = Math.max(60, minDuration)
    if (newMax >= minAllowedMax) {
      onMaxDurationChange(newMax)
    }
  }

  // Filter max options to only show values >= max(60 minutes, minDuration)
  // Max options should always start from 1 hour (60 minutes) as per requirements
  const availableMaxOptions = useMemo(() => {
    const minAllowedMax = Math.max(60, minDuration)
    return maxOptions.filter((option) => option.value >= minAllowedMax)
  }, [maxOptions, minDuration])

  return (
    <div className="session-duration-range">
      <h3 className="session-duration-range-title">Duração das sessões de estudo:</h3>
      <p className="session-duration-range-description">
        Defina a duração <strong>mínima</strong> e <strong>máxima</strong> para estudo focado em
        cada disciplina.
      </p>

      <div className="session-duration-range-container">
        <div className="session-duration-range-input-wrapper" data-tour="session-minmax">
          <div className="session-duration-range-select-group">
            <div className="session-duration-range-select-wrapper">
              <label htmlFor="min-duration" className="session-duration-range-label">
                Mínimo
              </label>
              <select
                id="min-duration"
                className="session-duration-range-select"
                value={minDuration}
                onChange={handleMinChange}
              >
                {minOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="session-duration-range-separator">até</div>
            <div className="session-duration-range-select-wrapper">
              <label htmlFor="max-duration" className="session-duration-range-label">
                Máximo
              </label>
              <select
                id="max-duration"
                className="session-duration-range-select"
                value={maxDuration}
                onChange={handleMaxChange}
              >
                {availableMaxOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SessionDurationRange
